import type { ContractDeploymentRecord } from '@/types'

export type TerminalCleanupPresentationCode =
  | 'notApplicable'
  | 'awaitingTerminalState'
  | 'expiredCleanupPending'
  | 'settlementCleanupPending'
  | 'fullRefundCleanupPending'
  | 'manualCloseCleanupPending'
  | 'cleanupQueued'
  | 'cleanupContinuing'
  | 'cleanupVerifying'
  | 'cleanupFailed'
  | 'cleanupSkipped'
  | 'alreadyClosedVerified'
  | 'expiredCleanupCompleted'
  | 'settlementCleanupCompleted'
  | 'fullRefundCleanupCompleted'
  | 'manualCloseCleanupCompleted'
  | 'recoveryCleanupCompleted'
  | 'projectionInconsistent'
  | 'unknown'

export interface TerminalCleanupPresentation {
  code: TerminalCleanupPresentationCode
  colorScheme: string
  source?: string
}

const completedEvidenceStatuses = new Set([
  'scars_completed',
  'scars_already_closed',
])
const knownCleanupSources = new Set([
  'expiry_cleanup',
  'settlement',
  'refund_terminal',
  'manual_admin',
  'recovery',
])

const isVerifiedCompletedEvidence = (
  record: ContractDeploymentRecord
): boolean => {
  const evidence = record.terminalCleanupEvidence
  return Boolean(
    evidence &&
      completedEvidenceStatuses.has(evidence.statusId) &&
      evidence.verificationStatus === 'verified' &&
      evidence.orderClosed
  )
}

const resolveCompletedPresentation = (
  source: string,
  statusId: string
): TerminalCleanupPresentation => {
  if (!knownCleanupSources.has(source)) {
    return { code: 'unknown', colorScheme: 'gray', source }
  }
  if (statusId === 'scars_already_closed') {
    return { code: 'alreadyClosedVerified', colorScheme: 'green', source }
  }

  switch (source) {
    case 'expiry_cleanup':
      return { code: 'expiredCleanupCompleted', colorScheme: 'orange' }
    case 'settlement':
      return { code: 'settlementCleanupCompleted', colorScheme: 'green' }
    case 'refund_terminal':
      return { code: 'fullRefundCleanupCompleted', colorScheme: 'purple' }
    case 'manual_admin':
      return { code: 'manualCloseCleanupCompleted', colorScheme: 'cyan' }
    case 'recovery':
      return { code: 'recoveryCleanupCompleted', colorScheme: 'teal' }
    default:
      return { code: 'unknown', colorScheme: 'gray' }
  }
}

export const resolveTerminalCleanupPresentation = (
  record: ContractDeploymentRecord
): TerminalCleanupPresentation => {
  const compatibility = record.chain_compatibility?.toLowerCase()
  if (!compatibility) {
    return { code: 'unknown', colorScheme: 'gray' }
  }
  if (compatibility !== 'solana') {
    return { code: 'notApplicable', colorScheme: 'gray' }
  }

  const evidence = record.terminalCleanupEvidence
  const projectedRecordId =
    record.terminalCleanupRecordId ?? record.terminal_cleanup_record_id
  const projectedState =
    record.terminalCleanupState ?? record.terminal_cleanup_state
  const completed = isVerifiedCompletedEvidence(record)
  const expectedSource =
    projectedState === 'expired_cleanup_completed'
      ? 'expiry_cleanup'
      : projectedState === 'refund_cleanup_completed'
        ? 'refund_terminal'
        : projectedState === 'manual_close_cleanup_completed'
          ? 'manual_admin'
          : undefined

  if (
    (projectedRecordId || projectedState) &&
    (!evidence ||
      (projectedRecordId && evidence.recordId !== projectedRecordId) ||
      (expectedSource && evidence.source !== expectedSource) ||
      !completed)
  ) {
    return { code: 'projectionInconsistent', colorScheme: 'red' }
  }

  if (!evidence) {
    switch (record.orderStatusId) {
      case 'ost_expired':
        return { code: 'expiredCleanupPending', colorScheme: 'yellow' }
      case 'ost_settled':
        return { code: 'settlementCleanupPending', colorScheme: 'yellow' }
      case 'ost_full_refunded':
        return { code: 'fullRefundCleanupPending', colorScheme: 'yellow' }
      case 'ost_closed':
        return { code: 'manualCloseCleanupPending', colorScheme: 'yellow' }
      default:
        return { code: 'awaitingTerminalState', colorScheme: 'gray' }
    }
  }

  if (completed) {
    return resolveCompletedPresentation(evidence.source, evidence.statusId)
  }

  switch (evidence.statusId) {
    case 'scars_pending':
      return {
        code: 'cleanupQueued',
        colorScheme: 'blue',
        source: evidence.source,
      }
    case 'scars_partial':
      return {
        code: 'cleanupContinuing',
        colorScheme: 'orange',
        source: evidence.source,
      }
    case 'scars_completed':
    case 'scars_already_closed':
      return {
        code: 'cleanupVerifying',
        colorScheme: 'blue',
        source: evidence.source,
      }
    case 'scars_failed':
      return {
        code: 'cleanupFailed',
        colorScheme: 'red',
        source: evidence.source,
      }
    case 'scars_skipped':
      return {
        code: 'cleanupSkipped',
        colorScheme: 'gray',
        source: evidence.source,
      }
    default:
      return { code: 'unknown', colorScheme: 'gray', source: evidence.source }
  }
}
